import React from "react";
import {
  Box,
  Typography,
  Container,
  Stack,
  Grid,
  Divider,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useGetAllBlogsQuery } from "../../../Store/api/rtk";
import BlogDetailsSkeleton from "./BlogDetailsSkeleton";
import BlogCart from "../../../Components/BlogCart";
import SeoHelmet from "../../../Components/SeoHelmet";

// Theme config
const darkTheme = {
  green: "#12C895",
  greenLow: "rgba(18, 200, 149, 0.05)",
  glassBg: "rgba(20, 26, 36, 0.6)",
  borderRaw: "rgba(255, 255, 255, 0.06)",
  textWhite: "#F8FAFC",
  textMuted: "#94A3B8",
};

// Sub-component: Header
const Header = ({ data }) => {
  // Render tags
  const tagsRender = data?.tags?.map((tag, index) => (
    <Typography
      key={index}
      variant="caption"
      sx={{
        color: darkTheme?.textMuted,
        mr: 2,
        cursor: "pointer",
        fontFamily: "monospace",
        "&:hover": { color: darkTheme?.green },
      }}
    >
      #{tag}
    </Typography>
  ));

  return (
    <Stack
      sx={{
        width: "100%",
        aspectRatio:"5/2",
        borderRadius: "16px",
        overflow: "hidden",
        border: `1px solid ${darkTheme?.borderRaw}`,
        mb: 6,
      }}
      direction="row"
    >
      <Box
        sx={{
          height: "100%",
          backgroundImage: `linear-gradient(to top, rgba(15, 20, 30, 0.9), transparent), url(${import.meta.env?.VITE_URL}${data?.header_image?.file_name})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          p: { xs: 3, md: 5 },
          width: "100%",
        }}
      >
        <Typography
          sx={{
            color: darkTheme?.green,
            fontFamily: "monospace",
            mb: 2,
            letterSpacing: "1px",
          }}
        >
          [ {new Date(data?.created_at * 1000)?.toLocaleDateString("fa-IR")} ]
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {tagsRender}
        </Box>
      </Box>
    </Stack>
  );
};

// Main Component: BlogDetails
const BlogDetails = () => {
  const { code } = useParams();

  // Fetch data
  const { data: blog, status } = useGetAllBlogsQuery({ code });
  const data = blog?.data?.data?.[0];
  const { data: newBlogs, status: newBlogStatus } = useGetAllBlogsQuery({});

  // Render related posts
  const relatedPostsRender = newBlogs?.data?.data
    ?.filter((post) => post?.code !== code)
    ?.slice(0, 4) // Optional limit
    ?.map((post) => (
      <Grid key={post?.code} size={{ xs: 12, sm: 6, lg: 12 }} sx={{ mb: 2 }}>
        <BlogCart blog={post} />
      </Grid>
    ));

  if (newBlogStatus !== "fulfilled" || status !== "fulfilled")
    return <BlogDetailsSkeleton />;

  // SEO config
  const seo = data?.seo;
  const defaultTitle = data?.title
    ? `${data?.title} - ویراتک`
    : "مقاله - ویراتک";
  const articleCoverImage = data?.header_image?.file_name
    ? `${import.meta.env?.VITE_URL}${data?.header_image?.file_name}`
    : undefined;

  return (
    <Box
      sx={{
        direction: "rtl",
        bgcolor: "transparent",
        minHeight: "100vh",
        pb: 10,
        "& *": { boxSizing: "border-box" },
      }}
    >
      <SeoHelmet
        title={defaultTitle}
        metaTitle={seo?.meta_title || defaultTitle}
        metaDescription={seo?.meta_description || ""}
        ogImage={
          seo?.og_image?.file_name
            ? `${import.meta.env?.VITE_URL}${seo?.og_image?.file_name}`
            : articleCoverImage
        }
      />

      <Container maxWidth="exl" sx={{ pt: { xs: 10, md: 15 } }}>
        {/* <Header data={data} /> */}

        <Grid container spacing={4} sx={{ position: "relative" }}>
          {/* Content Area */}
          {data?.title ? (
            <Grid size={{ xs: 12, lg: 8, xl: 9 }}>
              <Box
                sx={{
                  p: { xs: 3, md: 5 },
                  bgcolor: darkTheme?.glassBg,
                  backdropFilter: "blur(24px)",
                  borderRadius: "16px",
                  border: `1px solid ${darkTheme?.borderRaw}`,
                }}
              >
                <Typography
                  variant="h5"
                  component="h1"
                  fontWeight={700}
                  sx={{ color: darkTheme?.textWhite, mb: 4, lineHeight: 1.6 }}
                >
                  {data?.title}
                </Typography>

                <Divider sx={{ borderColor: darkTheme?.borderRaw, mb: 4 }} />

                <Stack
                  sx={{
                    color: darkTheme?.textMuted,
                    lineHeight: 2.2,
                    fontSize: "1.05rem",
                    "& a": { color: darkTheme?.green, textDecoration: "none" },
                    "& img": { maxWidth: "100%", borderRadius: "12px", my: 3 },
                    "& h2, & h3": { color: darkTheme?.textWhite, mt: 4, mb: 2 },
                  }}
                  dangerouslySetInnerHTML={{ __html: data?.body_data || "" }}
                />
              </Box>
            </Grid>
          ) : (
            <Typography
              sx={{
                p: 5,
                color: darkTheme?.textMuted,
                fontFamily: "monospace",
              }}
            >
              [ERROR: ARTICLE_NOT_FOUND]
            </Typography>
          )}

          {/* Sidebar */}
          <Grid
            size={{ xs: 12, lg: 4, xl: 3 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              position: "sticky",
              top: "100px",
              height: "max-content",
            }}
          >
            <Box
              sx={{
                mb: 3,
                pb: 2,
                borderBottom: `1px solid ${darkTheme?.borderRaw}`,
              }}
            >
              <Typography
                sx={{
                  color: darkTheme?.textWhite,
                  fontWeight: 800,
                  fontSize: "1.2rem",
                }}
              >
                مقالات <span style={{ color: darkTheme?.green }}>مرتبط</span>
              </Typography>
            </Box>
            <Grid container>{relatedPostsRender}</Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default BlogDetails;
